import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import { addPadAttachment, createPadEntry, removePadAttachment, updatePadEntry, uploadPadAttachmentFile } from "./api";
import PadAttachments from "./PadAttachments";
import {
  PAD_CATEGORIES,
  ALTERNATE_METHODS,
  STOP_REASONS,
  categoryMeta,
  totalRentFromItem,
  rentBreakdownFromItem,
  moveOutDateFromItem,
  money,
  monthOf,
} from "./padInfoTypes";

// Visual tokens — mirror the addendum modal so the two sections read as one product.
const C = {
  primary: "#0C447C",
  primaryMd: "#1A6FB5",
  border: "#E4E9F2",
  inputBorder: "#D6DEEA",
  muted: "#64748B",
  text: "#0F1F35",
  soft: "#F7F9FC",
};
const T = { title: 16, body: 13, label: 12, hint: 11 };

const styles = {
  body: { padding: "14px 18px", maxHeight: "70vh", overflowY: "auto", background: "#fff" },
  label: { fontSize: T.label, fontWeight: 600, color: C.text, marginBottom: 4, display: "block" },
  fieldBox: {
    display: "flex", alignItems: "center", gap: 8, height: 38,
    border: `1px solid ${C.inputBorder}`, borderRadius: 9, padding: "0 10px", background: "#fff",
  },
  bareInput: {
    border: "none", outline: "none", background: "transparent", flex: 1, width: "100%",
    fontSize: T.body, color: C.text, height: "100%", fontFamily: "inherit", minWidth: 0,
  },
  hint: { fontSize: T.hint, fontWeight: 500, color: C.muted, lineHeight: 1.4 },
};

const Field = ({ label, children, hint, style }) => (
  <div style={style}>
    <label style={styles.label}>{label}</label>
    {children}
    {hint ? <div style={{ ...styles.hint, marginTop: 3 }}>{hint}</div> : null}
  </div>
);

const Box = ({ children, className = "", style }) => (
  <div className={`pim-focusable ${className}`} style={{ ...styles.fieldBox, ...style }}>
    {children}
  </div>
);

/**
 * The category picker: one select-shaped button whose dropdown explains each option — the
 * label plus its one-line description — because a native <select> can only show bare text.
 */
const CategorySelect = ({ value, onPick, disabled }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const sel = value ? categoryMeta(value) : null;
  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        className="pim-ddbtn pim-focusable"
        onClick={() => !disabled && setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {sel ? (
          <>
            <span className="ic" style={{ background: sel.bg, color: sel.color }}>
              <FeatherIcon icon={sel.icon} size={14} />
            </span>
            <span className="lbl">{sel.label}</span>
          </>
        ) : (
          <span className="ph">Select the update type…</span>
        )}
        <FeatherIcon icon="chevron-down" size={15} color={C.muted} style={{ flex: "none", transform: open ? "rotate(180deg)" : "none", transition: "transform .12s" }} />
      </button>
      {open ? (
        <div className="pim-dd" role="listbox">
          {PAD_CATEGORIES.map((c) => (
            <button
              key={c.value}
              type="button"
              role="option"
              aria-selected={c.value === value}
              className={`pim-ddopt${c.value === value ? " on" : ""}`}
              onClick={() => {
                onPick(c.value);
                setOpen(false);
              }}
            >
              <span className="ic" style={{ background: c.bg, color: c.color }}>
                <FeatherIcon icon={c.icon} size={15} />
              </span>
              <span className="tx">
                <span className="t">{c.label}</span>
                <span className="b">{c.blurb}</span>
              </span>
              {c.value === value ? <FeatherIcon icon="check" size={14} color={C.primaryMd} style={{ flex: "none" }} /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const emptyForm = (tenantNames = "") => ({
  tenant_names: tenantNames,
  category: "",
  effective_date: "",
  delay: { month: "", resume_date: "" },
  temp_stop: { alternate_method: "", resume_date: "" },
  stop: { reason: "", move_out_date: "" },
  notes: "",
});

const fromExisting = (e) => ({
  tenant_names: e.tenant_names || "",
  category: e.category || "",
  effective_date: e.effective_date || "",
  delay: { month: "", resume_date: "", ...(e.delay || {}) },
  temp_stop: { alternate_method: "", resume_date: "", ...(e.temp_stop || {}) },
  stop: { reason: "", move_out_date: "", ...(e.stop || {}) },
  notes: e.notes || "",
});

/**
 * Create / edit one PAD Info entry — a single compact surface, sized for small laptop
 * screens: category select with the attachments box beside it, the category's fields below,
 * then notes. No money and no banking details are captured here: the rent is read back from
 * the unit's mastersheet row, and account information never enters this system.
 *
 * The resident is NOT a field — it already sits in the header, and it is never editable.
 * The names still travel in the payload (a New PAD takes the incoming lease's tenants).
 */
const PadInfoModal = ({ isOpen, toggle, itemId, item, unitNo, buildingName, propertyName, tenantNames, incoming, existing, voidCheque, onSaved }) => {
  const [form, setForm] = useState(() => (existing ? fromExisting(existing) : emptyForm(tenantNames)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Attachments are staged, not uploaded on pick: a brand-new entry has nothing to hang a file
  // on yet, and Cancel must not leave an orphaned object in the bucket. Everything is applied
  // in one go by save().
  const [stagedFiles, setStagedFiles] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const [savingStep, setSavingStep] = useState("");
  // If the entry saves but an upload then fails, the entry still exists. Remembering its id
  // means pressing Save again edits that entry rather than creating a second one.
  const [createdId, setCreatedId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setForm(existing ? fromExisting(existing) : emptyForm(tenantNames));
      setError("");
      setStagedFiles([]);
      setRemovedAttachmentIds([]);
      setSavingStep("");
      setCreatedId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existing?._id]);

  const meta = form.category ? categoryMeta(form.category) : null;
  const fields = meta ? meta.fields : [];
  // Rent is never entered or stored here — it is always the mastersheet's number.
  const totalRent = useMemo(() => totalRentFromItem(item), [item]);
  const rentBreakdown = useMemo(() => rentBreakdownFromItem(item), [item]);
  // A PAD Stop pre-fills its move-out date from the lease end column, but stays editable.
  const systemMoveOut = useMemo(() => moveOutDateFromItem(item), [item]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setSub = (key, patch) => setForm((f) => ({ ...f, [key]: { ...f[key], ...patch } }));

  const pickCategory = (value) => {
    const patch = { category: value };
    // A New PAD is usually for the incoming resident — the row may still show the departing one.
    if (value === "NEW" && !existing && incoming?.tenants && (!form.tenant_names || form.tenant_names === tenantNames)) patch.tenant_names = incoming.tenants;
    if (value !== "NEW" && !existing && form.tenant_names === incoming?.tenants) patch.tenant_names = tenantNames || "";
    // A New PAD starts when the lease starts — take the signed lease's start date.
    if (value === "NEW" && !existing && incoming?.start_date && !form.effective_date) patch.effective_date = incoming.start_date;
    // A PAD Stop is usually the end of the lease — offer that date, let them change it.
    if (value === "STOP" && systemMoveOut && !form.stop.move_out_date) patch.stop = { ...form.stop, move_out_date: systemMoveOut };
    set(patch);
  };

  // Documents still on the entry after the pending removals apply.
  const keptSavedCount = (existing?.attachments || []).filter(
    (a) => a && !a.removed && !removedAttachmentIds.includes(a.attachment_id)
  ).length;

  const validate = () => {
    if (!form.category) return "Pick what kind of PAD update this is.";
    if (!form.effective_date) return "The effective date is required.";
    if (form.category === "DELAY" && form.delay.resume_date && form.delay.resume_date < form.effective_date) return "The PAD cannot resume before the pause starts.";
    if (form.category === "TEMP_STOP" && form.temp_stop.resume_date && form.temp_stop.resume_date < form.effective_date) return "The PAD cannot resume before the pause starts.";
    if (form.category === "TEMP_STOP" && !form.temp_stop.alternate_method) return "How is the resident paying meanwhile?";
    if (form.category === "STOP" && !form.stop.reason) return "Please pick a reason for stopping the PAD.";
    // Every entry needs its paperwork — the email, notice or signed form behind the change.
    if (keptSavedCount + stagedFiles.length === 0) return "Attach at least one document — the paperwork behind this change.";
    return "";
  };

  const save = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setSaving(true);
    setError("");
    const payload = {
      mastersheet_item_id: itemId,
      category: form.category,
      effective_date: form.effective_date,
      // The bank always pulls on the 1st; a Delay moves that month's pull to the chosen day.
      pull_day: form.category === "DELAY" ? Number(form.effective_date.slice(8, 10)) || 1 : 1,
      notes: form.notes,
      tenant_names: (form.tenant_names || tenantNames || "").trim(),
    };
    if (form.category === "DELAY") payload.delay = { month: monthOf(form.effective_date), resume_date: form.delay.resume_date };
    if (form.category === "TEMP_STOP") payload.temp_stop = form.temp_stop;
    if (form.category === "STOP") payload.stop = form.stop;
    try {
      // 1. The entry itself — a new one has to exist before anything can hang off it.
      setSavingStep(stagedFiles.length || removedAttachmentIds.length ? "Saving entry…" : "");
      let entryId = existing?._id || createdId;
      if (entryId) {
        await updatePadEntry(entryId, payload);
      } else {
        const res = await createPadEntry(payload);
        entryId = res?.data?.data?._id;
        if (!entryId) {
          if (stagedFiles.length) throw new Error("The entry saved but did not return an id, so the files were not attached.");
        } else {
          setCreatedId(entryId);
        }
      }

      // 2. Removals before uploads, so swapping a file for a better one cannot trip the
      //    ten-attachment ceiling.
      for (const attachmentId of [...removedAttachmentIds]) {
        setSavingStep("Removing attachments…");
        await removePadAttachment(entryId, attachmentId);
        // Drop it as it lands: a retry after a later failure must not repeat finished work.
        setRemovedAttachmentIds((prev) => prev.filter((x) => x !== attachmentId));
      }

      // 3. Upload each staged file, then record it against the entry.
      const toUpload = [...stagedFiles];
      for (let i = 0; i < toUpload.length; i++) {
        const file = toUpload[i];
        setSavingStep(`Uploading ${i + 1} of ${toUpload.length}…`);
        const uploaded = await uploadPadAttachmentFile(file);
        await addPadAttachment(entryId, {
          key: uploaded.key,
          filename: file.name,
          size: file.size,
          mime: file.type,
        });
        setStagedFiles((prev) => prev.filter((f) => f !== file));
      }

      const n = toUpload.length;
      toast.success(
        `${existing?._id ? "PAD info updated" : "PAD info recorded"}${n ? ` · ${n} file${n === 1 ? "" : "s"} attached` : ""}`
      );
      onSaved && onSaved();
      toggle();
    } catch (e) {
      const base = e?.response?.data?.message || e.message || "Failed to save PAD info";
      // Say plainly that the entry is already saved, or someone will re-type the whole thing.
      const msg = createdId || existing?._id ? `${base} The entry itself is saved — press Save again to finish the attachments.` : base;
      setError(msg);
      toast.error(base);
    } finally {
      setSaving(false);
      setSavingStep("");
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered className="pad-info-modal-dialog" contentClassName="pad-info-modal-content">
      <style>{`
        /* A bit wider than lg so the two-column top row breathes; still fits a 13" laptop. */
        .pad-info-modal-dialog { max-width: 860px; }
        .pad-info-modal-content { border: none; border-radius: 14px; overflow: hidden; box-shadow: 0 24px 70px rgba(12,68,124,0.20); }
        .pad-info-modal-content input::placeholder, .pad-info-modal-content textarea::placeholder { color: #9AA7B8; }
        .pad-info-modal-content select:focus, .pad-info-modal-content input:focus, .pad-info-modal-content textarea:focus { outline: none; }
        .pim-focusable:focus-within { border-color: ${C.primaryMd}; box-shadow: 0 0 0 2px rgba(26,111,181,0.12); }

        .pim-toprow { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 14px; align-items: start; }
        @media (max-width: 640px) { .pim-toprow { grid-template-columns: 1fr; } }

        /* The category select and its descriptive dropdown. */
        .pim-ddbtn { display: flex; align-items: center; gap: 8px; width: 100%; height: 38px; padding: 0 10px;
          border: 1px solid ${C.inputBorder}; border-radius: 9px; background: #fff; cursor: pointer;
          font-family: inherit; text-align: left; }
        .pim-ddbtn .ic { width: 24px; height: 24px; border-radius: 7px; flex: none; display: inline-flex;
          align-items: center; justify-content: center; }
        .pim-ddbtn .lbl { flex: 1; min-width: 0; font-size: 13px; font-weight: 700; color: ${C.text};
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .pim-ddbtn .ph { flex: 1; font-size: 13px; color: #9AA7B8; }
        .pim-dd { position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 30; background: #fff;
          border: 1px solid ${C.border}; border-radius: 11px; box-shadow: 0 14px 40px rgba(12,68,124,0.16);
          padding: 4px; max-height: 320px; overflow-y: auto; }
        .pim-ddopt { display: flex; align-items: center; gap: 10px; width: 100%; padding: 8px 9px; border: none;
          border-radius: 8px; background: none; cursor: pointer; font-family: inherit; text-align: left; }
        .pim-ddopt:hover { background: ${C.soft}; }
        .pim-ddopt.on { background: #EFF6FD; }
        .pim-ddopt .ic { width: 30px; height: 30px; border-radius: 8px; flex: none; display: inline-flex;
          align-items: center; justify-content: center; }
        .pim-ddopt .tx { flex: 1; min-width: 0; }
        .pim-ddopt .t { display: block; font-size: 13px; font-weight: 700; color: ${C.text}; line-height: 1.2; }
        .pim-ddopt .b { display: block; font-size: 11.5px; color: ${C.muted}; line-height: 1.35; margin-top: 1px; }

        .pim-fields { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; margin-top: 12px; }
        .pim-btn { border: none; cursor: pointer; font-size: 13px; font-weight: 600; border-radius: 8px;
          padding: 9px 16px; display: inline-flex; align-items: center; gap: 7px; font-family: inherit; }
        .pim-btn-primary { background: ${C.primary}; color: #fff; box-shadow: 0 6px 18px rgba(12,68,124,0.28); }
        .pim-btn-primary:hover:not(:disabled) { background: #083460; }
        .pim-btn-primary:disabled { background: #94A3B8; box-shadow: none; cursor: not-allowed; }
        .pim-btn-ghost { background: #fff; color: ${C.text}; border: 1px solid ${C.inputBorder}; }
        .pim-btn-ghost:hover { background: #F1F5FA; }
        .pim-iconbtn { background: none; border: none; cursor: pointer; color: ${C.muted}; display: inline-flex; }
        .pim-iconbtn:hover { color: ${C.text}; }
        .pim-select { border: none; outline: none; background: transparent; flex: 1; width: 100%; font-size: ${T.body}px; color: ${C.text}; height: 100%; font-family: inherit; }
      `}</style>

      {/* ---- header: what this is, and who/where — the resident lives here, nowhere else ---- */}
      <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "12px 18px", borderBottom: `1px solid ${C.border}`, background: "#fff" }}>
        <span style={{ width: 34, height: 34, borderRadius: 9, background: "#EBF3FC", color: C.primary, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
          <FeatherIcon icon="credit-card" size={17} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: T.title, fontWeight: 700, color: C.text, lineHeight: 1.2 }}>
            {existing?._id ? "Edit PAD info" : "Record PAD info"}
          </div>
          <div style={{ fontSize: T.hint, color: C.muted, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {[unitNo ? `Unit ${unitNo}` : "", buildingName, propertyName, tenantNames].filter(Boolean).join(" · ")}
          </div>
        </div>
        <button type="button" className="pim-iconbtn" onClick={toggle} aria-label="Close">
          <FeatherIcon icon="x" size={18} />
        </button>
      </div>

      <div style={styles.body}>
        {/* ---- category + attachments, side by side ---- */}
        <div className="pim-toprow">
          <Field label="What kind of PAD update is this?">
            <CategorySelect value={form.category} onPick={pickCategory} disabled={saving} />
          </Field>
          <Field label="Attachments" hint="At least one document is required — the email, notice or signed form.">
            <PadAttachments
              entryId={existing?._id || createdId}
              saved={existing?.attachments || []}
              staged={stagedFiles}
              removedIds={removedAttachmentIds}
              onStage={(files) => setStagedFiles((prev) => [...prev, ...files])}
              onUnstage={(i) => setStagedFiles((prev) => prev.filter((_, idx) => idx !== i))}
              onToggleRemove={(id) =>
                setRemovedAttachmentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
              }
              voidCheque={voidCheque}
              canWrite
              busy={saving}
            />
          </Field>
        </div>

        {/* ---- the fields this category needs ---- */}
        {meta && (
          <div className="pim-fields">
            <Field
              label={meta.dateLabel || "Effective from"}
              hint={
                form.category === "NEW" && incoming?.start_date && form.effective_date === incoming.start_date
                  ? "Taken from the signed lease's start date."
                  : undefined
              }
            >
              <Box>
                <FeatherIcon icon="calendar" size={14} color={C.muted} />
                <input type="date" style={styles.bareInput} value={form.effective_date} onChange={(e) => set({ effective_date: e.target.value })} />
              </Box>
            </Field>

            {fields.includes("delay_resume") && (
              <Field label="PAD resumes on" hint="Leave blank if open-ended.">
                <Box>
                  <FeatherIcon icon="play-circle" size={14} color={C.muted} />
                  <input type="date" style={styles.bareInput} value={form.delay.resume_date} onChange={(e) => setSub("delay", { resume_date: e.target.value })} />
                </Box>
              </Field>
            )}

            {fields.includes("temp_resume") && (
              <Field label="PAD resumes on" hint="Leave blank if open-ended.">
                <Box>
                  <FeatherIcon icon="play-circle" size={14} color={C.muted} />
                  <input type="date" style={styles.bareInput} value={form.temp_stop.resume_date} onChange={(e) => setSub("temp_stop", { resume_date: e.target.value })} />
                </Box>
              </Field>
            )}

            {fields.includes("temp_method") && (
              <Field label="Paying meanwhile by">
                <Box>
                  <select className="pim-select" value={form.temp_stop.alternate_method} onChange={(e) => setSub("temp_stop", { alternate_method: e.target.value })}>
                    <option value="">Select…</option>
                    {ALTERNATE_METHODS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </Box>
              </Field>
            )}

            {fields.includes("stop_reason") && (
              <Field label="Reason">
                <Box>
                  <select className="pim-select" value={form.stop.reason} onChange={(e) => setSub("stop", { reason: e.target.value })}>
                    <option value="">Select…</option>
                    {STOP_REASONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </Box>
              </Field>
            )}

            {fields.includes("stop_move_out") && (
              <Field
                label="Move-out date"
                hint={systemMoveOut && form.stop.move_out_date === systemMoveOut ? "From the lease end date — edit if it differs." : undefined}
              >
                <Box>
                  <FeatherIcon icon="log-out" size={14} color={C.muted} />
                  <input type="date" style={styles.bareInput} value={form.stop.move_out_date} onChange={(e) => setSub("stop", { move_out_date: e.target.value })} />
                </Box>
              </Field>
            )}

            {/* Total rent — read-only, always the mastersheet's number. */}
            <Field
              label="Total rent"
              hint={
                rentBreakdown.length
                  ? `${rentBreakdown.map((r) => `${r.label} ${money(r.amount)}`).join(" + ")} — change via Addendum or Lease.`
                  : "No rent columns on this unit's mastersheet row yet."
              }
            >
              <Box style={{ background: C.soft }}>
                <FeatherIcon icon="dollar-sign" size={14} color={C.muted} />
                <span style={{ ...styles.bareInput, display: "flex", alignItems: "center", fontWeight: 700 }}>
                  {totalRent === null ? "—" : money(totalRent)}
                </span>
                <FeatherIcon icon="lock" size={12} color={C.muted} />
              </Box>
            </Field>
          </div>
        )}

        {/* ---- notes ---- */}
        {meta && (
          <Field label="Notes for accounting" style={{ marginTop: 12 }}>
            <div className="pim-focusable" style={{ border: `1px solid ${C.inputBorder}`, borderRadius: 9, background: "#fff", padding: "7px 10px", display: "flex", gap: 8 }}>
              <FeatherIcon icon="file-text" size={14} color={C.muted} style={{ flex: "none", marginTop: 3 }} />
              <textarea
                rows={2}
                style={{ ...styles.bareInput, height: "auto", resize: "vertical", width: "100%", lineHeight: 1.4 }}
                placeholder="Anything accounting should know — e.g. “Sending e-transfer for Jan”, “SD received”…"
                value={form.notes}
                onChange={(e) => set({ notes: e.target.value })}
              />
            </div>
          </Field>
        )}
      </div>

      {/* ---- footer ---- */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 18px", borderTop: `1px solid ${C.border}`, background: "#fff" }}>
        <span style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 7 }}>
          {error ? (
            <>
              <FeatherIcon icon="alert-circle" size={14} color="#A32D2D" style={{ flex: "none" }} />
              <span style={{ ...styles.hint, color: "#A32D2D", fontWeight: 600 }}>{error}</span>
            </>
          ) : null}
        </span>
        <div style={{ display: "flex", gap: 8, flex: "none" }}>
          <button
            type="button"
            className="pim-btn pim-btn-ghost"
            onClick={() => {
              // A failed save may have already created the entry — refresh so the list is honest.
              if (createdId) onSaved && onSaved();
              toggle();
            }}
            disabled={saving}
          >
            {createdId ? "Close" : "Cancel"}
          </button>
          <button type="button" className="pim-btn pim-btn-primary" onClick={save} disabled={saving || !meta}>
            {saving ? <Spinner size="sm" /> : <FeatherIcon icon="save" size={14} />}
            {saving && savingStep ? savingStep : existing?._id ? "Save changes" : "Record PAD info"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PadInfoModal;
